import { useEffect, useMemo, useState } from "react";
import type {
  Contract,
  ContractCost,
  ContractIn,
  EnergyFlow,
  ScenarioParams,
} from "./lib/types";
import { api } from "./lib/api";
import { ScenarioBar } from "./components/ScenarioBar";
import { FlowSummary } from "./components/FlowSummary";
import { ContractCard } from "./components/ContractCard";
import { ContractEditor } from "./components/ContractEditor";
import { MonthlyOverview } from "./components/MonthlyOverview";
import { ImportContractModal } from "./components/ImportContractModal";
import { ContractComparisonChart } from "./charts/ContractComparisonChart";
import { DayInLifeChart } from "./charts/DayInLifeChart";
import { PriceDurationCurveChart } from "./charts/PriceDurationCurveChart";

const DEFAULT_SCENARIO: ScenarioParams = {
  year: 2026,
  accu_bruto_kwh: 8.20,
  accu_marge_pct: 80,
  accu_strategy: "conservative",
  accu_charge_kw: 2.4,
  curtailment_enabled: false,
  curtailment_threshold_eur_per_kwh: 0.0,
};

const EMPTY_CONTRACT: ContractIn = {
  name: "Nieuw contract",
  type: "dynamisch",
  notes: "",
  vastrecht_eur_per_maand: 6.5,
  energiebelasting_eur_per_kwh: 0.0916,
  btw_pct: 21,
  kale_prijs_afname_eur_per_kwh: null,
  terugleververgoeding_eur_per_kwh: null,
  inkoopvergoeding_eur_per_kwh: 0.02,
  inkoopvergoeding_handling: "uitgekeerd",
  terugleverkosten_model: "geen",
  terugleverkosten_vast_eur_per_maand: 0,
  terugleverkosten_per_kwh: 0,
  niet_saldering_pct: 50,
};

export default function App() {
  const [scenario, setScenario] = useState<ScenarioParams>(DEFAULT_SCENARIO);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [costs, setCosts] = useState<ContractCost[]>([]);
  const [flow, setFlow] = useState<EnergyFlow | null>(null);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const cs = await api.listContracts();
        setContracts(cs);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, []);

  // Re-evaluate on scenario change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [f, cs] = await Promise.all([
          api.simulate(scenario),
          api.evaluateAll(scenario),
        ]);
        if (!cancelled) {
          setFlow(f);
          setCosts(cs);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scenario, contracts.length]);

  const sortedContracts = useMemo(() => {
    if (costs.length === 0) return contracts;
    const order = new Map(costs.map((c, i) => [c.contract_id, i]));
    return [...contracts].sort(
      (a, b) => (order.get(a.id) ?? 999) - (order.get(b.id) ?? 999)
    );
  }, [contracts, costs]);

  const lowestId = costs[0]?.contract_id ?? null;

  const handleSave = async (id: number, draft: ContractIn) => {
    try {
      const updated = await api.updateContract(id, draft);
      setContracts((cs) => cs.map((c) => (c.id === id ? updated : c)));
      const newCosts = await api.evaluateAll(scenario);
      setCosts(newCosts);
      setEditing(null);
      setError(null);
    } catch (e) {
      setError("Opslaan mislukt: " + (e as Error).message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.deleteContract(id);
      setContracts((cs) => cs.filter((c) => c.id !== id));
      setCosts((cs) => cs.filter((c) => c.contract_id !== id));
      setEditing(null);
      setError(null);
    } catch (e) {
      setError("Verwijderen mislukt: " + (e as Error).message);
    }
  };

  const handleAdd = async () => {
    try {
      const created = await api.createContract({ ...EMPTY_CONTRACT });
      setContracts((cs) => [...cs, created]);
      setEditing(created);
      setError(null);
    } catch (e) {
      setError("Aanmaken mislukt: " + (e as Error).message);
    }
  };

  const handleImport = async (c: ContractIn) => {
    try {
      const created = await api.createContract(c);
      setContracts((cs) => [...cs, created]);
      setImportOpen(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 py-6 md:px-6 md:py-8">
      <header className="mb-7">
        <div className="flex items-center gap-2.5 mb-2">
          <span className="tag tag-green">Casa de los Gatos</span>
          <span className="help mono">v0.1</span>
        </div>
        <h1 className="serif m-0 text-[38px] font-normal leading-tight" style={{ letterSpacing: "-0.03em" }}>
          Energiecontract
          <br />
          <span className="italic text-green">vergelijker</span>
        </h1>
        <p className="help mt-2.5 max-w-[600px]">
          Op basis van werkelijke productie en verbruik 2025. Vul tarieven in van de contracten die
          je overweegt en zie direct het effect onder verschillende scenario's.
        </p>
      </header>

      {error && (
        <div className="bg-warn-bg text-warn p-3 rounded-md mb-4 text-sm">
          Fout: {error}
        </div>
      )}

      <ScenarioBar scenario={scenario} setScenario={setScenario} />

      {flow && (
        <div className="mt-4">
          <FlowSummary flow={flow} scenario={scenario} />
        </div>
      )}

      <section className="mt-8">
        <div className="flex justify-between items-baseline mb-1 flex-wrap gap-3">
          <h2 className="serif m-0 text-2xl font-medium">Contracten</h2>
          <div className="flex gap-2 flex-wrap">
            <button className="btn" onClick={() => setImportOpen(true)}>
              Importeer via Claude
            </button>
            <button className="btn btn-primary" onClick={handleAdd}>
              + Nieuw contract
            </button>
          </div>
        </div>
        <div className="help mb-3.5">
          Gesorteerd op jaarkosten <strong>na optimale curtailment</strong> per contract.
          Curtailment wordt automatisch toegepast wanneer het netto goedkoper is.
        </div>

        {loading && contracts.length > 0 && (
          <div className="help text-center py-2">Berekening loopt…</div>
        )}

        <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {sortedContracts.map((c) => {
            const cost = costs.find((co) => co.contract_id === c.id);
            if (!cost) return null;
            return (
              <ContractCard
                key={c.id}
                contract={c}
                cost={cost}
                isLowest={c.id === lowestId}
                onEdit={setEditing}
              />
            );
          })}
        </div>

        {contracts.length === 0 && (
          <div className="card text-center py-8">
            <p className="m-0 text-ink-muted">
              Nog geen contracten. Klik op "+ Nieuw contract" om te beginnen.
            </p>
          </div>
        )}
      </section>

      {flow && costs.length > 0 && (
        <section className="card mt-6 p-5">
          <ContractComparisonChart costs={costs} />
        </section>
      )}

      {flow && contracts.length > 0 && (
        <section className="mt-6">
          <MonthlyOverview contracts={sortedContracts} costs={costs} flow={flow} scenario={scenario} />
        </section>
      )}

      {flow && (
        <section className="card mt-6 p-5">
          <DayInLifeChart flow={flow} />
        </section>
      )}

      {flow && (
        <section className="card mt-6 p-5">
          <PriceDurationCurveChart prices={flow.price_duration_curve} />
        </section>
      )}

      <footer className="mt-12 pt-5 border-t border-line text-ink-muted text-xs flex justify-between flex-wrap gap-3">
        <span>
          Marktprijs data: energy‑charts.info (Fraunhofer ISE), CC BY 4.0. Solar/verbruik
          gemodelleerd op werkelijke maandtotalen 2025.
        </span>
      </footer>

      {editing && flow && (
        <ContractEditor
          contract={editing}
          flow={flow}
          scenario={scenario}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setEditing(null)}
        />
      )}

      {importOpen && (
        <ImportContractModal onImport={handleImport} onClose={() => setImportOpen(false)} />
      )}
    </div>
  );
}
